import * as Dialog from '@radix-ui/react-dialog';
import axios from 'axios';
import { AlertTriangle, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { UpdatePositionStopSchema } from 'shared';

import { useUpdatePositionStop } from '../../features/positions/mutations';
import type { PositionDto } from '../../lib/api/endpoints';
import { Button } from '../ui/Button';
import { FormField } from '../ui/FormField';

interface EditStopDialogProps {
  position: PositionDto;
  trigger?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

interface StopFormValues {
  stopPrice: number;
  reason: string;
}

interface ApiErrorBody {
  error?: { message?: string };
}

function stopUpdateErrorMessage(error: unknown): string {
  if (axios.isAxiosError<ApiErrorBody>(error)) {
    return error.response?.data.error?.message ?? 'Could not update stop.';
  }
  return 'Could not update stop.';
}

function isAdverseStop(position: PositionDto, stopPrice: number): boolean {
  if (!position.currentStop || Number.isNaN(stopPrice)) return false;
  const currentStop = Number(position.currentStop);
  return position.side === 'LONG' ? stopPrice < currentStop : stopPrice > currentStop;
}

export function EditStopDialog({
  position,
  trigger,
  open: controlledOpen,
  onOpenChange,
}: EditStopDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const updateStop = useUpdatePositionStop();
  const form = useForm<StopFormValues>({
    defaultValues: {
      stopPrice: position.currentStop
        ? Number(position.currentStop)
        : Number(position.currentPrice),
      reason: '',
    },
  });
  const watchedStop = form.watch('stopPrice');
  const adverse = useMemo(
    () => isAdverseStop(position, Number(watchedStop)),
    [position, watchedStop],
  );
  const correctionRequired = position.stopCorrectionReason != null;
  const wideningBlocked = adverse && !correctionRequired;
  const reason = form.watch('reason');
  const reasonTooShort = correctionRequired && reason.trim().length < 10;

  function reset(nextOpen: boolean) {
    if (controlledOpen === undefined) setInternalOpen(nextOpen);
    onOpenChange?.(nextOpen);
    if (!nextOpen) {
      form.reset({
        stopPrice: position.currentStop
          ? Number(position.currentStop)
          : Number(position.currentPrice),
        reason: '',
      });
    }
  }

  const onSubmit = form.handleSubmit((values) => {
    if (wideningBlocked) {
      form.setError('stopPrice', { message: 'A valid stop may only stay unchanged or tighten.' });
      return;
    }
    if (correctionRequired && values.reason.trim().length < 10) {
      form.setError('reason', { message: 'Reason must be at least 10 characters.' });
      return;
    }

    const body = UpdatePositionStopSchema.parse({
      stopPrice: values.stopPrice,
      ...(values.reason.trim() ? { reason: values.reason.trim() } : {}),
      ...(correctionRequired ? { correction: true as const } : {}),
    });

    updateStop.mutate(
      {
        accountId: position.accountId,
        symbol: position.symbol,
        body,
      },
      {
        onSuccess: () => reset(false),
      },
    );
  });

  return (
    <Dialog.Root open={open} onOpenChange={reset}>
      {trigger ? <Dialog.Trigger asChild>{trigger}</Dialog.Trigger> : null}
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/60" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded border border-border bg-surface p-5 text-text shadow-xl">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Dialog.Title className="text-lg font-semibold">Edit Stop</Dialog.Title>
              <Dialog.Description className="mt-1 text-sm text-text-muted">
                {position.symbol} | current {position.currentStop ?? 'not set'}
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <Button aria-label="Close stop dialog" size="sm" type="button" variant="ghost">
                <X className="h-4 w-4" aria-hidden="true" />
              </Button>
            </Dialog.Close>
          </div>

          <form className="mt-5 space-y-4" onSubmit={onSubmit}>
            <FormField
              label="Stop price"
              type="number"
              step="0.000001"
              error={form.formState.errors.stopPrice?.message}
              {...form.register('stopPrice', {
                valueAsNumber: true,
                min: { value: 0.000001, message: 'Stop price must be positive.' },
              })}
            />

            {correctionRequired ? (
              <div className="flex gap-2 rounded border border-warn/70 bg-warn-subtle p-3 text-sm text-amber-100">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" aria-hidden="true" />
                <p>
                  The stored stop is{' '}
                  {position.stopCorrectionReason === 'PREVIOUS_EPISODE'
                    ? 'from an earlier position episode'
                    : 'invalid for the current price'}
                  . Record the correction reason before saving.
                </p>
              </div>
            ) : adverse ? (
              <div className="flex gap-2 rounded border border-danger/70 bg-danger/10 p-3 text-sm text-danger">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" aria-hidden="true" />
                <p>A valid stop may only stay unchanged or tighten.</p>
              </div>
            ) : null}

            <div className="space-y-2">
              <label className="block text-sm font-medium text-text" htmlFor="stop-reason">
                Reason
              </label>
              <textarea
                id="stop-reason"
                className="min-h-24 w-full rounded border border-border bg-bg-muted px-3 py-2 text-sm text-text outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/30"
                aria-invalid={Boolean(form.formState.errors.reason)}
                aria-describedby={form.formState.errors.reason ? 'stop-reason-error' : undefined}
                {...form.register('reason', {
                  validate: (value) =>
                    !correctionRequired ||
                    value.trim().length >= 10 ||
                    'Reason must be at least 10 characters.',
                })}
              />
              {form.formState.errors.reason ? (
                <p id="stop-reason-error" className="text-sm text-danger">
                  {form.formState.errors.reason.message}
                </p>
              ) : null}
            </div>

            {updateStop.isError ? (
              <p className="text-sm text-danger">{stopUpdateErrorMessage(updateStop.error)}</p>
            ) : null}

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Dialog.Close asChild>
                <Button type="button" variant="secondary">
                  Cancel
                </Button>
              </Dialog.Close>
              <Button
                type="submit"
                disabled={updateStop.isPending || reasonTooShort || wideningBlocked}
              >
                {updateStop.isPending ? 'Saving...' : 'Save stop'}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
